import {
    HttpErrorResponse,
    HttpInterceptorFn,
    HttpRequest,
    HttpResponse,
} from "@angular/common/http";
import { inject } from "@angular/core";
import {
    NOTIFICATIONS_CONFIG,
    NotificationsConfig,
} from "@Front-end/seed/seed-config";
import {
    ErrorHandlingOptions,
    ResponseError,
} from "@Front-end/seed/utils/interfaces";
import { SimpleMap } from "@Front-end/ui/interfaces";
import { NotificationStatuses } from "@Front-end/ui/models/notifications";
import { QueueNotificationService } from "@Front-end/ui/modules/notifications";
import { TranslateService } from "@ngx-translate/core";
import * as Sentry from "@sentry/angular";
import {
    catchError,
    map,
    Observable,
    throwError,
} from "rxjs";
import { errorTitle } from "../constants/common.constants";

export const errorNotificationInterceptor: HttpInterceptorFn = (req, next) => {

    const httpNotificationsConfig: NotificationsConfig = inject(NOTIFICATIONS_CONFIG);
    const queueNotificationService: QueueNotificationService = inject(QueueNotificationService);
    const translateService: TranslateService = inject(TranslateService);

    const deleteExtraParameters = (request: HttpRequest<unknown>): HttpRequest<unknown> => {
        Object.keys(patternParams).map((item: string) => {
            request.params.delete(item);
            const currentStatement: RegExp = patternParams[item];
            request = Object.assign(request, {
                urlWithParams: request.urlWithParams.replace(currentStatement, ""),
            });
        });
        return request;
    };
    const patternParams: SimpleMap<RegExp> = {
        errorHandling: /\W{1}errorHandling=[a-zA-Z]+/,
        consoleNotify: /\W{1}consoleNotify=[a-zA-Z]+/,
        consoleMessage: /\W{1}consoleMessage=[\w\W]+/,
    };

    const errorHandle = req.params.get("errorHandling");
    const consoleNotification = req.params.get("consoleNotify");
    const consoleNotificationMessage = req.params.get("consoleMessage");

    if (errorHandle || consoleNotification || consoleNotificationMessage) {
        req = deleteExtraParameters(req);
    }

    return next(req).pipe(
        map((event) => {
            if (
                event instanceof HttpResponse
                && event.status === 200
                && (event?.body as { error: ResponseError })?.error
                && (event?.body as { error: ResponseError })?.error?.code
                && errorHandle !== ErrorHandlingOptions.ignore
            ) {

                const { code, message } = (event?.body as { error: ResponseError }).error;

                // Capture error in Sentry
                Sentry.captureMessage(`API Error: ${code} - ${message}`, {
                    level: "error",
                    tags: {
                        errorCode: code?.toString(),
                        errorType: "NOTIFICATION_ERROR",
                    },
                    extra: {
                        responseBody: event?.body,
                    },
                });

                // TODO Allow notifications for code 500 with "Bonus not found" message even if 500 is in blockList
                const isBonusActivationError = code?.toString() === "500" && message === "Bonus not found";
                const allowed: boolean = isBonusActivationError || !httpNotificationsConfig.blockList
                    ?.some((blockListItem: number | string) => blockListItem?.toString() === code?.toString());
                allowed && queueNotificationService.addNotification({
                    label: translateService.instant(errorTitle),
                    message,
                    status: NotificationStatuses.Error,
                });
            }
            return event;
        }),
        catchError((error: HttpErrorResponse): Observable<never> => {
            // Capture HTTP error in Sentry
            Sentry.captureException(error, {
                tags: {
                    errorStatus: error.status?.toString(),
                    errorType: "HTTP_ERROR",
                    url: error.url || "unknown",
                },
                extra: {
                    errorMessage: error.message,
                    errorName: error.name,
                    statusText: error.statusText,
                },
            });

            return throwError(() => error);
        }),
    );
};
